import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import Button from '../../../shared/components/ui/Button';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useAuth, useAuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { convertArabicToWesternNumerals } from '../../../shared/utils/numberUtils';
import { useLockoutCountdown, formatCountdown } from '../../../hooks/useLockoutCountdown';
import { WaitKind, isIdentityExists, retryAfterOf, waitKeys, waitKindOf } from '../../../shared/utils/authErrors';

const COUNTRY_CODE = '964';

// Iraqi mobile = 10 digits starting 75/76/77/78/79.
const IRAQI_MOBILE_RE = /^7[5-9]\d{8}$/;
// Normalize anything the doctor pastes/types into the local 10-digit form:
// strips spaces/symbols, a +964/964 country code, and a leading 0.
const normalizeIraqiLocal = (raw: string): string => {
  let d = (raw || '').replace(/\D/g, '');
  if (d.startsWith('00964')) d = d.slice(5);
  else if (d.startsWith('964')) d = d.slice(3);
  d = d.replace(/^0+/, '');
  return d.slice(0, 10);
};
const isValidIraqiMobile = (local: string): boolean => IRAQI_MOBILE_RE.test(local);

/**
 * Phone + OTP step for a QR-verified (or manually submitted) registration.
 * The phone can be changed freely until the OTP is confirmed. Uses the same OTP input
 * component (react-otp-input, 6 boxes, auto-submit) as the normal login OtpScreen.
 */
const QrPhoneEntryScreen = () => {
  const [step, setStep] = useState<'enter_phone' | 'enter_otp'>('enter_phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 409 qr.identity.exists: the licence already has an account → sign in with licence + phone on file.
  const [identityExists, setIdentityExists] = useState(false);
  // 429 otp.rate_limited: live countdown from details.retry_after.
  const { secondsLeft: lockSeconds, isLocked, start: startLockout } = useLockoutCountdown();
  const [waitKind, setWaitKind] = useState<WaitKind>('send');

  const authService = useAuth();
  const { checkAuth } = useAuthContext();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('qr_pending_token') || '';

  const handleSendOtp = async () => {
    if (!isValidIraqiMobile(phone)) {
      setError(t('qr_invalid_phone'));
      return;
    }
    if (!token) {
      setError(t('qr_session_expired'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await authService.qrSendOtp(token, COUNTRY_CODE, phone);
      if (result.isSuccess) {
        setStep('enter_otp');
      } else if (isIdentityExists(result.errorDetails)) {
        setIdentityExists(true);
      } else if (retryAfterOf(result.errorDetails) > 0) {
        setWaitKind('send');
        startLockout(retryAfterOf(result.errorDetails));
      } else {
        setError(result.error || t('qr_verify_failed'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('qr_verify_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = useCallback(async () => {
    if (loading || otp.length !== 6 || isLocked) return;
    setLoading(true);
    setError(null);
    try {
      const result = await authService.qrVerifyOtp(token, COUNTRY_CODE, phone, otp);
      if (result.isSuccess) {
        sessionStorage.removeItem('qr_pending_token');
        sessionStorage.removeItem('qr_prefill');
        // Flip AuthContext to authenticated BEFORE navigating, otherwise the
        // ProtectedRoute bounces back to /login (re-asking license + OTP).
        await checkAuth();
        navigate('/doctor_info_registration', { replace: true });
      } else if (isIdentityExists(result.errorDetails)) {
        setOtp('');
        setIdentityExists(true);
      } else if (retryAfterOf(result.errorDetails) > 0) {
        setOtp('');
        setWaitKind(waitKindOf('verify', result.errorDetails?.message_code));
        startLockout(retryAfterOf(result.errorDetails));
      } else {
        setOtp('');
        setError(result.error || t('invalid_otp'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('invalid_otp'));
    } finally {
      setLoading(false);
    }
  }, [authService, checkAuth, navigate, otp, phone, token, t, loading, isLocked, startLockout]);

  // Auto-submit when OTP is complete (6 digits) — same UX as OtpScreen.
  useEffect(() => {
    if (otp.length === 6 && !loading && !isLocked) handleVerifyOtp();
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToLogin = () => {
    sessionStorage.removeItem('qr_pending_token');
    sessionStorage.removeItem('qr_prefill');
    navigate('/login', { replace: true });
  };

  if (identityExists) {
    return (
      <ResponsiveFormLayout
        showBackButton={true}
        onBack={() => { setIdentityExists(false); setStep('enter_phone'); setOtp(''); }}
        showLanguageToggle={true}
        icon="/ima_logo.png"
        showIcon={true}
        iconSize="w-40 h-32"
        title={t('qr_identity_exists_title')}
        actions={<Button onClick={goToLogin} text={t('sign_in_with_licence')} />}
        maxHeight="95vh"
      >
        <div className="w-full rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-4 text-center text-sm text-amber-900 leading-relaxed" role="alert">
          {t('qr_identity_exists_body')}
        </div>
      </ResponsiveFormLayout>
    );
  }

  const waitBox = isLocked ? (
    <div className="w-full rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 text-center mt-3" role="status">
      <div className="text-sm font-semibold text-red-600">{t(waitKeys(waitKind).title)}</div>
      <div className="mt-1 text-xs text-red-500">{t(waitKeys(waitKind).body)}</div>
      <div className="mt-1 font-mono text-2xl font-bold text-red-600" dir="ltr">{formatCountdown(lockSeconds)}</div>
    </div>
  ) : null;

  if (step === 'enter_phone') {
    return (
      <ResponsiveFormLayout
        showBackButton={true}
        onBack={() => navigate('/scan_id')}
        showLanguageToggle={true}
        icon="/ima_logo.png"
        showIcon={true}
        iconSize="w-40 h-32"
        title={t('qr_enter_phone_title')}
        subtitle={t('qr_enter_phone_subtitle')}
        actions={<Button onClick={handleSendOtp} text={t('confirm_and_send_otp')} loading={loading} disabled={!isValidIraqiMobile(phone) || loading || isLocked} />}
        maxHeight="95vh"
      >
        <div className="w-full">
          <div className="flex items-center gap-2" dir="ltr">
            <span className="px-3 py-3 rounded-2xl border-2 border-gray-300 text-[#2e5d7d] font-bold">+{COUNTRY_CODE}</span>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={phone}
              onChange={(e) => { setPhone(normalizeIraqiLocal(e.target.value)); setError(null); }}
              placeholder="7XXXXXXXXX"
              className={`flex-1 px-4 py-3 text-lg border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2e5d7d] ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
          {/* Hint so the doctor knows the rule even before submitting */}
          <div className="text-xs text-[#726c6c] mt-2">{t('iraqi_phone_hint')}</div>
          {waitBox}
          {error && <div className="text-red-500 text-sm mt-2 text-center">{error}</div>}
        </div>
      </ResponsiveFormLayout>
    );
  }

  return (
    <ResponsiveFormLayout
      showBackButton={true}
      onBack={() => { setStep('enter_phone'); setOtp(''); setError(null); }}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      showIcon={true}
      iconSize="w-40 h-32"
      title={t('verify_phone')}
      subtitle={t('enter_code_sent_to')}
      actions={
        <button
          type="button"
          onClick={() => { setStep('enter_phone'); setOtp(''); setError(null); }}
          className="w-full min-h-[48px] px-8 py-3 rounded-2xl text-base font-semibold text-[#2e5d7d] bg-gray-100 hover:bg-gray-200 border-2 border-gray-200"
        >
          {t('change_phone_number')}
        </button>
      }
      maxHeight="95vh"
      centerContent={true}
    >
      <div className="w-full">
        <div className="text-center text-[#2e5d7d] text-lg font-bold mb-4" dir="ltr">+{COUNTRY_CODE}{phone}</div>
        {/* Same OTP component as the normal login flow */}
        <div className="w-full flex justify-center mb-4">
          <div className="otp-input-container">
            <OtpInput
              value={otp}
              onChange={(value: string) => {
                setOtp(convertArabicToWesternNumerals(value));
                setError(null);
              }}
              numInputs={6}
              renderSeparator={<span></span>}
              renderInput={(props) => <input {...props} />}
              inputType="tel"
              shouldAutoFocus
            />
          </div>
        </div>
        {loading && (
          <div className="text-center text-[#726c6c] text-sm mb-2">{t('verifying_payment') || '...'}</div>
        )}
        {waitBox}
        {error && <div className="text-red-500 text-sm mt-2 text-center">{error}</div>}
      </div>
    </ResponsiveFormLayout>
  );
};

export default QrPhoneEntryScreen;
